import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CustomButton from "../../../components/common/Button/CustomButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import AspectRatioOutlinedIcon from "@mui/icons-material/AspectRatioOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import PropertiesAPI from "../../../api/firebase/PropertiesAPI";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import AllProductContext from "../../../context/allProducts/AllProductsContext";
import {
  Avatar,
  Button, Stack, TextField, InputAdornment, MenuItem, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AddProperty from "../../../components/admin/add-property/AddProperty";
import AllPropertiesContext from "../../../context/allProperties/AllPropertiesContext";
import AddPropertyContext from "../../../context/addProperty/AddPropertyContext";
import { styles } from "./styles";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";
import {
  isProductLinkedToProperty,
  getProductsUsingProperty,
  defaultStarterProperties,
} from "../../../utils/propertyProducts";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const getCssAspectRatio = (ratioStr, fallback = "3 / 2") => {
  if (!ratioStr || typeof ratioStr !== "string") return fallback;
  return ratioStr.trim().replace(":", " / ") || fallback;
};

export const formatINR = (val) => {
  const num = Number(val);
  if (isNaN(num)) return "₹0";
  return "₹" + num.toLocaleString("en-IN");
};

export const stripHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
};


export {
  isProductLinkedToProperty,
  getProductsUsingProperty,
  defaultStarterProperties,
};

const PropertiesListAdmin = () => {
  const initState = {
    id: "",
    label: "",
    imageUrl: "",
    imageName: "",
    link: "/collections",
    accent: "#10b981",
    aspectRatio: "3:2",
    order: 1,
    isActive: true,
  };

  const propertiesAPI = useMemo(() => new PropertiesAPI(), []);
  const productsAPI = useMemo(() => new ProductsAPI(), []);
  const allPropertiesContext = useContext(AllPropertiesContext);
  const propertyContext = useContext(AddPropertyContext);
  const allProductsContext = useContext(AllProductContext);

  const allProductsContextRef = useRef(allProductsContext);
  useEffect(() => {
    allProductsContextRef.current = allProductsContext;
  }, [allProductsContext]);

  const products = useMemo(() => {
    return Array.isArray(allProductsContext?.state)
      ? allProductsContext.state
      : [];
  }, [allProductsContext?.state]);

  const [activeTab, setActiveTab] = useState("preview"); // "preview" | "table"

  // Quick View & Direct Product Linking Modal State
  const [quickViewProperty, setQuickViewProperty] = useState(null);
  const [quickViewTab, setQuickViewTab] = useState(0); // 0 = Linked Products, 1 = Link Products
  const [productSearch, setProductSearch] = useState("");
  const [linkFilter, setLinkFilter] = useState("ALL"); // "ALL", "UNLINKED", "LINKED"
  const [linkingProductId, setLinkingProductId] = useState(null);
  const [quickProductDetail, setQuickProductDetail] = useState(null);
  const [quickDetailImageIndex, setQuickDetailImageIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [visibility, setVisibility] = useState("ALL");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSectionEnabled, setIsSectionEnabled] = useState(true);
  const [savingSection, setSavingSection] = useState(false);
  const { setState: setProperties } = allPropertiesContext;
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await propertiesAPI.getProperties();
      if (!res?.success) throw new Error("Load failed");
      let list = res.data || [];
      if (list.length === 0) {
        list = defaultStarterProperties;
        setProperties(list);
        Promise.all(
          defaultStarterProperties.map((item) =>
            propertiesAPI.setProperty({
              id: item.id,
              payload: item,
            })
          )
        ).catch((seedErr) => {
          console.log("Auto-seed default properties error:", seedErr);
        });
      } else {
        setProperties([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
      if (typeof propertiesAPI.getSectionSettings === "function") {
        try {
          const settingsRes = await propertiesAPI.getSectionSettings();
          if (settingsRes?.success && settingsRes.data) {
            const enabled = settingsRes.data.isEnabled ?? settingsRes.data.enabled ?? true;
            setIsSectionEnabled(Boolean(enabled));
          }
        } catch (settingsErr) {
          console.log("Could not load section settings:", settingsErr);
        }
      }

      if (typeof productsAPI.getProducts === "function") {
        try {
          if (!allProductsContextRef.current?.state || allProductsContextRef.current.state.length === 0) {
            const prodRes = await productsAPI.getProducts();
            if (prodRes && prodRes.data && allProductsContextRef.current?.setState) {
              allProductsContextRef.current.setState(prodRes.data);
            }
          }
        } catch (prodErr) {
          console.log("Could not load products for property counts:", prodErr);
        }
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [propertiesAPI, productsAPI, setProperties]);
  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  // Modal State
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);

  const showAddModal = (action) => {
    if (action === "ADD") {
      setEditModal(false);
      const nextOrder = Math.max(0, ...allPropertiesContext.state.map((item) => Number(item.order) || 0)) + 1;
      propertyContext.setState({
        ...initState,
        order: nextOrder,
      });
    }
    setOpenModal(true);
  };

  const hideModal = () => { if (!saving) setOpenModal(false); };

  // Snackbar State
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = () => setSnackbarState(true);
  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  // Set Edit Property
  const setEditProperty = (prop) => {
    setEditModal(true);
    propertyContext.setState(prop);
    setOpenModal(true);
  };

  // Drag and drop reordering state
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const dragStartedRef = useRef(false);

  const handleDragStart = (e, item) => {
    dragStartedRef.current = true;
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverItemId !== item.id) {
      setDragOverItemId(item.id);
    }
  };

  const handleDragLeave = (e, item) => {
    e.preventDefault();
    if (dragOverItemId === item.id) {
      setDragOverItemId(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
    setTimeout(() => {
      dragStartedRef.current = false;
    }, 150);
  };

  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData("text/plain") || draggedItemId;
    setDraggedItemId(null);
    setDragOverItemId(null);
    setTimeout(() => {
      dragStartedRef.current = false;
    }, 150);

    if (!sourceId || sourceId === targetItem.id) return;

    const currentSorted = [...allPropertiesContext.state].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const currentActive = currentSorted.filter((p) => p.isActive !== false);

    const sourceIndex = currentActive.findIndex((p) => p.id === sourceId);
    const targetIndex = currentActive.findIndex((p) => p.id === targetItem.id);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const reorderedActive = [...currentActive];
    const [moved] = reorderedActive.splice(sourceIndex, 1);
    reorderedActive.splice(targetIndex, 0, moved);

    const updatedWithOrder = reorderedActive.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    const orderMap = new Map(updatedWithOrder.map((item) => [item.id, item.order]));
    const previousState = allPropertiesContext.state;

    const newState = previousState.map((p) => {
      if (orderMap.has(p.id)) {
        return { ...p, order: orderMap.get(p.id) };
      }
      return p;
    });

    allPropertiesContext.setState(newState);

    const changedItems = updatedWithOrder.filter((item) => {
      const original = previousState.find((p) => p.id === item.id);
      return !original || original.order !== item.order;
    });

    try {
      await Promise.all(
        changedItems.map((item) =>
          propertiesAPI.setProperty({
            id: item.id,
            payload: { order: item.order },
          })
        )
      );
      setMessage({ text: "Properties reordered successfully!", type: "success" });
      showSnackbar();
    } catch (err) {
      console.log("Error saving reordered properties:", err);
      allPropertiesContext.setState(previousState);
      setMessage({ text: "Failed to update property order.", type: "error" });
      showSnackbar();
    }
  };

  // Toggle active switch
  const handleToggleActive = async (item, event) => {
    if (event) {
      event.stopPropagation();
    }
    const newStatus = item.isActive === false ? true : false;
    const previousState = allPropertiesContext.state;
    allPropertiesContext.setState(
      previousState.map((p) =>
        p.id === item.id ? { ...p, isActive: newStatus } : p
      )
    );

    try {
      const res = await propertiesAPI.setProperty({
        id: item.id,
        payload: { isActive: newStatus },
      });
      if (res?.success) {
        setMessage({
          text: `Property ${newStatus ? "activated" : "hidden"} successfully!`,
          type: "success",
        });
        showSnackbar();
      } else {
        throw new Error(res?.message || "Failed to update status");
      }
    } catch (err) {
      console.log("Error toggling active status:", err);
      allPropertiesContext.setState(previousState);
      setMessage({ text: "Failed to update status.", type: "error" });
      showSnackbar();
    }
  };

  // Toggle homepage section visibility
  const handleToggleSectionVisibility = async () => {
    if (savingSection) return;
    const nextState = !isSectionEnabled;
    setSavingSection(true);
    try {
      if (typeof propertiesAPI.setSectionSettings === "function") {
        const res = await propertiesAPI.setSectionSettings({ isEnabled: nextState });
        if (res?.success) {
          setIsSectionEnabled(nextState);
          setMessage({
            text: `"Shop By Properties" section is now ${nextState ? "enabled" : "disabled"} on the homepage!`,
            type: "success",
          });
          showSnackbar();
        } else {
          setMessage({
            text: "Failed to update section visibility.",
            type: "error",
          });
          showSnackbar();
        }
      } else {
        setIsSectionEnabled(nextState);
      }
    } catch (err) {
      console.log("Error updating section visibility:", err);
      setMessage({
        text: "Error occurred while updating section visibility.",
        type: "error",
      });
      showSnackbar();
    } finally {
      setSavingSection(false);
    }
  };

  // Delete Property
  const handleDeleteProperty = async (propertyId) => {
    if (deleting) return;
    setDeleting(true);
    const target = allPropertiesContext.state.find((p) => p.id === propertyId);
    const imageName = target ? target.imageName : null;

    try {
      const res = await propertiesAPI.deleteProperty(propertyId, imageName);
      if (res.success) {
        setMessage({
          text: "Property category deleted successfully!",
          type: "success",
        });
        showSnackbar();
        setDeleteTarget(null);
        const updated = allPropertiesContext.state.filter(
          (p) => p.id !== propertyId
        );
        allPropertiesContext.setState(updated);
      } else {
        setMessage({
          text: "Failed to delete property. Please try again.",
          type: "error",
        });
        showSnackbar();
      }
    } catch (err) {
      console.log("Delete property error:", err);
      setMessage({
        text: "Error occurred while deleting property.",
        type: "error",
      });
      showSnackbar();
    } finally {
      setDeleting(false);
    }
  };

  // Add / Update Property Submit
  const handleSetProperty = async (
    action = "ADD",
    property_id = null,
    payload = null
  ) => {
    if (saving) return;
    setSaving(true);
    const isVisibilityToggle = payload !== null;
    const targetId =
      property_id ||
      propertyContext.state.id ||
      `PROP_${Date.now()}`;

    let dataToSave;
    if (isVisibilityToggle) {
      dataToSave = payload;
    } else {
      dataToSave = {
        label: propertyContext.state.label || "",
        imageUrl: propertyContext.state.imageUrl || propertyContext.state.image || "",
        imageName: propertyContext.state.imageName || "",
        link: propertyContext.state.link || "/collections",
        accent: propertyContext.state.accent || "#10b981",
        aspectRatio: propertyContext.state.aspectRatio || "3:2",
        order: Number(propertyContext.state.order) || 1,
        isActive: propertyContext.state.isActive !== false,
      };
    }

    try {
      const res = await propertiesAPI.setProperty({
        id: targetId,
        payload: dataToSave,
      });

      if (res.success) {
        setMessage({
          text: isVisibilityToggle
            ? "Status updated!"
            : action === "ADD"
            ? "Property added successfully!"
            : "Property updated successfully!",
          type: "success",
        });
        showSnackbar();
        setOpenModal(false);
        fetchProperties();
      } else {
        setMessage({
          text: "Failed to save property. Please try again.",
          type: "error",
        });
        showSnackbar();
      }
    } catch (err) {
      console.log("Save property error:", err);
      setMessage({
        text: "Error occurred while saving property.",
        type: "error",
      });
      showSnackbar();
    } finally {
      setSaving(false);
    }
  };

  // Seed default 5 properties into Firestore
  const handleSeedDefaults = async () => {
    if (
      !window.confirm(
        "This will initialize the 5 default Ayurvedic properties in Firestore. Continue?"
      )
    ) {
      return;
    }

    try {
      for (let i = 0; i < defaultStarterProperties.length; i++) {
        const item = defaultStarterProperties[i];
        const propId = `PROP_DEFAULT_${i + 1}`;
        await propertiesAPI.setProperty({
          id: propId,
          payload: item,
        });
      }
      setMessage({
        text: "Default 5 properties seeded successfully!",
        type: "success",
      });
      showSnackbar();
      fetchProperties();
    } catch (err) {
      console.log("Error seeding default properties:", err);
      setMessage({
        text: "Failed to seed defaults.",
        type: "error",
      });
      showSnackbar();
    }
  };

  const propertiesList = useMemo(() => {
    return allPropertiesContext.state || [];
  }, [allPropertiesContext.state]);

  // Map propertyId to assigned products count
  const propertyProductCountMap = useMemo(() => {
    const map = {};
    propertiesList.forEach((prop) => {
      map[prop.id] = getProductsUsingProperty(products, prop).length;
    });
    return map;
  }, [propertiesList, products]);

  const activeProductDetail = useMemo(() => {
    if (!quickProductDetail) return null;
    return products.find((p) => p.id === quickProductDetail.id) || quickProductDetail;
  }, [products, quickProductDetail]);

  // Linked products for quick view modal
  const linkedProductsForQuickView = useMemo(() => {
    if (!quickViewProperty) return [];
    return getProductsUsingProperty(products, quickViewProperty);
  }, [quickViewProperty, products]);

  const filteredLinkedProducts = useMemo(() => {
    if (!productSearch.trim()) return linkedProductsForQuickView;
    const query = productSearch.trim().toLowerCase();
    return linkedProductsForQuickView.filter((prod) => {
      const matchTitle = (prod.title || "").toLowerCase().includes(query);
      const matchId = (prod.id || "").toLowerCase().includes(query);
      const matchCat = (prod.parentCategory?.name || "").toLowerCase().includes(query);
      return matchTitle || matchId || matchCat;
    });
  }, [linkedProductsForQuickView, productSearch]);

  // Product counts for currently selected property modal
  const currentPropertyCounts = useMemo(() => {
    if (!quickViewProperty) return { linked: 0, unlinked: 0 };
    let linked = 0;
    products.forEach((p) => {
      if (isProductLinkedToProperty(p, quickViewProperty)) linked += 1;
    });
    return {
      linked,
      unlinked: Math.max(0, products.length - linked),
    };
  }, [products, quickViewProperty]);

  // Catalog products for Link Products tab
  const catalogProductsForLinkTab = useMemo(() => {
    if (!quickViewProperty) return [];
    let list = products;

    if (linkFilter === "LINKED") {
      list = list.filter((p) => isProductLinkedToProperty(p, quickViewProperty));
    } else if (linkFilter === "UNLINKED") {
      list = list.filter((p) => !isProductLinkedToProperty(p, quickViewProperty));
    }

    if (productSearch.trim()) {
      const q = productSearch.trim().toLowerCase();
      list = list.filter((p) => {
        const matchTitle = (p.title || "").toLowerCase().includes(q);
        const matchId = (p.id || "").toLowerCase().includes(q);
        const matchCat = (p.parentCategory?.name || "").toLowerCase().includes(q);
        return matchTitle || matchId || matchCat;
      });
    }

    return list;
  }, [products, quickViewProperty, linkFilter, productSearch]);

  // Suggested products to display in the empty state
  const suggestedProductsForEmptyState = useMemo(() => {
    if (!quickViewProperty || products.length === 0) return [];
    const propLabel = (quickViewProperty.label || "").toLowerCase();
    const words = propLabel.split(/\s+/).filter((w) => w.length > 2);

    let matches = products.filter((p) => {
      if (isProductLinkedToProperty(p, quickViewProperty)) return false;
      const title = (p.title || "").toLowerCase();
      const cat = (p.parentCategory?.name || "").toLowerCase();
      return words.some((w) => title.includes(w) || cat.includes(w));
    });

    if (matches.length === 0) {
      matches = products.filter((p) => !isProductLinkedToProperty(p, quickViewProperty)).slice(0, 3);
    } else {
      matches = matches.slice(0, 3);
    }

    return matches;
  }, [quickViewProperty, products]);

  // Handle direct linking / unlinking of a product to a property
  const handleToggleLinkProduct = async (product, property) => {
    if (!product || !property) return;
    const isLinked = isProductLinkedToProperty(product, property);
    const propId = String(property.id || "").trim();
    const propLabel = String(property.label || property.title || "").trim();

    const currentProps = Array.isArray(product.properties)
      ? [...product.properties]
      : typeof product.properties === "string" && product.properties
      ? [product.properties]
      : [];

    let updatedProps;
    if (isLinked) {
      updatedProps = currentProps.filter((p) => {
        if (!p) return false;
        if (typeof p === "string") {
          return p.trim() !== propId && p.trim().toLowerCase() !== propLabel.toLowerCase();
        }
        if (typeof p === "object") {
          const pId = String(p.id || "").trim();
          const pLabel = String(p.label || p.title || "").trim().toLowerCase();
          return pId !== propId && pLabel !== propLabel.toLowerCase();
        }
        return true;
      });
    } else {
      updatedProps = [...currentProps, propId];
    }

    setLinkingProductId(product.id);
    try {
      const res = await productsAPI.setProduct({
        id: product.id,
        payload: {
          properties: updatedProps,
        },
      });

      if (res?.success) {
        const updatedProducts = (allProductsContext.state || []).map((p) =>
          p.id === product.id ? { ...p, properties: updatedProps } : p
        );
        allProductsContext.setState(updatedProducts);

        setMessage({
          text: isLinked
            ? `Product "${product.title}" unlinked from "${propLabel}".`
            : `Product "${product.title}" linked to "${propLabel}" successfully!`,
          type: "success",
        });
        showSnackbar();
      } else {
        throw new Error(res?.message || "Failed to update product");
      }
    } catch (err) {
      console.error("Error linking/unlinking product:", err);
      setMessage({
        text: "Failed to update product property association.",
        type: "error",
      });
      showSnackbar();
    } finally {
      setLinkingProductId(null);
    }
  };

  const handleCloseQuickView = () => {
    setQuickViewProperty(null);
    setProductSearch("");
    setQuickViewTab(0);
    setLinkFilter("ALL");
    setQuickProductDetail(null);
  };

  // DataGrid Columns Definition
  const columns = [
    {
      field: "imageUrl",
      headerName: "Image",
      width: 75,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={params.row.imageUrl}
          alt={params.row.label}
          sx={{ width: 42, height: 54, my: 0.5, border: "1px solid #e0e0e0" }}
        />
      ),
    },
    {
      field: "label",
      headerName: "Property Category",
      width: 200,
      renderCell: (params) => (
        <div className="rowitem" style={{ fontWeight: 700, color: "#143833" }}>
          {params.row.label || "-"}
        </div>
      ),
    },
    {
      field: "totalProducts",
      headerName: "Total Products",
      width: 215,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => params.row.totalProducts ?? (propertyProductCountMap[params.row.id] || 0),
      renderCell: (params) => {
        const count = params.value ?? (propertyProductCountMap[params.row.id] || 0);
        return (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Click to view linked products" arrow>
              <Chip
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickViewTab(0);
                  setQuickViewProperty(params.row);
                  setProductSearch("");
                }}
                icon={<Inventory2OutlinedIcon sx={{ fontSize: "14px !important", color: count > 0 ? "#075e54" : "#94a3b8" }} />}
                label={count === 1 ? "1 Product" : `${count} Products`}
                size="small"
                sx={{
                  height: 25,
                  fontSize: "0.74rem",
                  fontWeight: 700,
                  bgcolor: count > 0 ? "#ecfdf5" : "#f1f5f9",
                  color: count > 0 ? "#065f46" : "#64748b",
                  border: `1px solid ${count > 0 ? "#a7f3d0" : "#e2e8f0"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: count > 0 ? "#d1fae5" : "#e2e8f0",
                    borderColor: count > 0 ? "#059669" : "#cbd5e1",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                }}
              />
            </Tooltip>
            <Tooltip title={`Link products directly to ${params.row.label}`} arrow>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddLinkRoundedIcon sx={{ fontSize: "14px !important" }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickViewTab(1);
                  setQuickViewProperty(params.row);
                  setProductSearch("");
                  setLinkFilter("ALL");
                }}
                sx={{
                  height: 25,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 1,
                  borderRadius: "6px",
                  borderColor: "#cbd5e1",
                  color: "#004d40",
                  bgcolor: "#ffffff",
                  minWidth: "auto",
                  "&:hover": {
                    borderColor: "#059669",
                    bgcolor: "#ecfdf5",
                    color: "#059669",
                  },
                }}
              >
                + Link
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "aspectRatio",
      headerName: "Aspect Ratio",
      width: 170,
      renderCell: (params) => {
        const sorted = [...propertiesList].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        const activeList = sorted.filter((p) => p.isActive !== false);
        const isSingleLast =
          activeList.length % 2 === 1 &&
          activeList[activeList.length - 1]?.id === params.row.id;
        const ratioText = params.row.aspectRatio || (isSingleLast ? "16:9 (Banner)" : "3:2 (Landscape)");
        return (
          <Chip
            icon={<AspectRatioOutlinedIcon style={{ fontSize: 13, color: "#065f46" }} />}
            label={ratioText}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.72rem",
              fontWeight: 700,
              bgcolor: "#eef7f4",
              color: "#065f46",
              border: "1px solid #bbf7d0",
            }}
          />
        );
      },
    },
    {
      field: "isActive",
      headerName: "Homepage Status",
      width: 170,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            size="small"
            checked={params.row.isActive !== false}
            onChange={(e) => handleToggleActive(params.row, e)}
            color="primary"
          />
          <Chip
            label={params.row.isActive !== false ? "Active" : "Hidden"}
            color={params.row.isActive !== false ? "success" : "default"}
            size="small"
            variant={params.row.isActive !== false ? "filled" : "outlined"}
            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
          />
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      width: 180,
      renderCell: (params) => {
        const formatted = formatDateTimeForAdmin(params.row.updatedAt || params.row.createdAt);
        return <div className="rowitem" style={{ fontSize: "0.82rem", color: "#64748b" }}>{formatted}</div>;
      },
    },
    { field: "id", hide: true },
  ];

  const filteredProperties = propertiesList.filter((item) =>
    [item.label, item.id].some((value) => String(value || "").toLowerCase().includes(search.trim().toLowerCase())) &&
    (visibility === "ALL" || (visibility === "ACTIVE" ? item.isActive !== false : item.isActive === false))
  );
  const rows = filteredProperties.map((item, index) => ({
    ...item,
    totalProducts: propertyProductCountMap[item.id] ?? 0,
    key: index,
  }));

  const sortedProperties = [...propertiesList].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const activeProperties = sortedProperties.filter((p) => p.isActive !== false);
  const inactiveProperties = sortedProperties.filter((p) => p.isActive === false);


  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      {/* Page Title & Actions Banner */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          color: "#ffffff",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: "#a7f3d0", fontWeight: 800, fontSize: "0.75rem", letterSpacing: "0.1em" }}>
              Homepage Layout Controls
            </Typography>
            <Chip
              label={`${propertiesList.length} Categories`}
              size="small"
              sx={{ height: 20, bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 700, fontSize: "0.68rem" }}
            />
          </Box>
          <Typography component="h1" variant="h4" fontWeight={800} sx={{ fontSize: { xs: "1.75rem", sm: "2.2rem" }, letterSpacing: "-0.02em" }}>
            Shop by Properties
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", maxWidth: 540, mt: 0.5 }}>
            Manage homepage properties, images, layout, and display order.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button startIcon={<RefreshIcon />} onClick={fetchProperties} disabled={loading || saving || deleting} sx={{ color: "#ffffff", bgcolor: "rgba(255,255,255,0.15)", borderRadius: "10px", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>{loading ? "Loading…" : "Refresh"}</Button>
          {!loading && !loadError && propertiesList.length === 0 && (
            <CustomButton
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={handleSeedDefaults}
              sx={{
                borderColor: "#34d399",
                color: "#34d399",
                "&:hover": { borderColor: "#6ee7b7", bgcolor: "rgba(52, 211, 153, 0.1)" },
              }}
            >
              Seed 5 Starter Properties
            </CustomButton>
          )}
          <CustomButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => showAddModal("ADD")}
            disabled={loading || loadError}
            sx={{
              bgcolor: "#ffffff", color: "#004d40", fontWeight: 800,
              px: 3, py: 1.2, borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "#f8fafc", color: "#00382e" },
            }}
          >
            Add Property
          </CustomButton>
        </Box>
      </Box>

      {/* Section Visibility Toggle Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: isSectionEnabled ? "#bbf7d0" : "#fed7aa",
          bgcolor: isSectionEnabled ? "#f0fdf4" : "#fffbeb",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isSectionEnabled ? "#dcfce7" : "#ffedd5",
              color: isSectionEnabled ? "#15803d" : "#c2410c",
              flexShrink: 0,
            }}
          >
            {isSectionEnabled ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={800} color="#1e293b">
                Homepage "Shop By Properties" Section
              </Typography>
              <Chip
                label={isSectionEnabled ? "Active on Homepage" : "Hidden from Homepage"}
                size="small"
                color={isSectionEnabled ? "success" : "warning"}
                sx={{ fontWeight: 700, fontSize: "0.75rem", height: 22 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {isSectionEnabled
                ? "The Ayurvedic properties collage section is live and displayed on your storefront homepage."
                : "The section is currently disabled. Customers will not see the Shop By Properties section on the homepage."}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}>
          {savingSection && <CircularProgress size={18} sx={{ color: "#64748b" }} />}
          <FormControlLabel
            control={
              <Switch
                checked={isSectionEnabled}
                onChange={handleToggleSectionVisibility}
                disabled={loading || savingSection}
                color="success"
                inputProps={{ "aria-label": "Toggle Shop By Properties section visibility" }}
              />
            }
            label={
              <Typography fontWeight={700} fontSize="0.9rem" color="#1e293b">
                {isSectionEnabled ? "Section Enabled" : "Section Disabled"}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        </Stack>
      </Paper>

      {loadError && <MuiAlert severity="error" sx={{ mb: 3, borderRadius: 2 }} action={<Button color="inherit" onClick={fetchProperties}>Retry</Button>}>Could not load properties. Please try again.</MuiAlert>}
      {loading && <Skeleton variant="rounded" height={300} sx={{ mb: 3 }} />}
      {/* Tabs: Visual Collage Preview vs Data Table */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Property views"
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none", fontSize: "0.95rem" },
            "& .Mui-selected": { color: "#075e54" },
            "& .MuiTabs-indicator": { bgcolor: "#075e54", height: 3 },
          }}
        >
          <Tab icon={<ViewQuiltOutlinedIcon />} iconPosition="start" label="Collage preview" value="preview" />
          <Tab icon={<TableChartOutlinedIcon />} iconPosition="start" label="Manage properties" value="table" />
        </Tabs>
      </Box>

      {/* Tab 1: Live Collage Visualizer */}
      {!loading && !loadError && activeTab === "preview" && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#0f2820">
                  Properties Grid Visualizer & Quick Editor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Live preview of storefront properties (max 2 per row). Drag and drop cards to reorder, or click any card to edit.
                </Typography>
              </Box>
            </Box>
          </Box>

          {!isSectionEnabled && (
            <MuiAlert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
              <strong>Notice:</strong> The &quot;Shop By Properties&quot; section is currently disabled. Visitors will not see this on the homepage until you enable it using the switch above.
            </MuiAlert>
          )}

          {propertiesList.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center", bgcolor: "#f8faf9", borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} color="#0f2820" mb={1}>
                No Categories Configured Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Seed the 5 starter Ayurvedic properties to immediately launch your homepage properties section.
              </Typography>
              <CustomButton variant="contained" startIcon={<AutoFixHighIcon />} onClick={handleSeedDefaults} sx={{ bgcolor: "#075e54" }}>
                Seed 5 Starter Properties
              </CustomButton>
            </Paper>
          ) : (
            <>
              {/* ── Active Properties Grid (Max 2 per row) ── */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: "18px",
                  alignItems: "start",
                }}
              >
                {activeProperties.map((card, idx) => {
                  const isSingleLast =
                    activeProperties.length % 2 === 1 &&
                    idx === activeProperties.length - 1;
                  const cardAspect = getCssAspectRatio(
                    card.aspectRatio,
                    isSingleLast ? "16 / 9" : "3 / 2"
                  );
                  const isDragging = draggedItemId === card.id;
                  const isDragOver = dragOverItemId === card.id && draggedItemId !== card.id;

                  return (
                    <Box
                      key={card.id || idx}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, card)}
                      onDragOver={(e) => handleDragOver(e, card)}
                      onDragLeave={(e) => handleDragLeave(e, card)}
                      onDrop={(e) => handleDrop(e, card)}
                      onDragEnd={handleDragEnd}
                      onClick={() => {
                        if (dragStartedRef.current) return;
                        setEditProperty(card);
                      }}
                      sx={{
                        width: "100%",
                        height: "auto",
                        aspectRatio: cardAspect,
                        borderRadius: "20px",
                        ...(isSingleLast ? { gridColumn: { sm: "1 / -1" } } : {}),
                        overflow: "hidden",
                        position: "relative",
                        cursor: "grab",
                        "&:active": { cursor: "grabbing" },
                        boxShadow: isDragOver
                          ? "0 0 0 3px #10b981, 0 16px 36px rgba(16, 185, 129, 0.3)"
                          : "0 4px 14px rgba(0,0,0,0.10)",
                        border: isDragOver ? "2px dashed #10b981" : "2px solid transparent",
                        opacity: isDragging ? 0.4 : 1,
                        transform: isDragOver ? "scale(1.02)" : "none",
                        transition: "transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: isDragOver ? "scale(1.02)" : "translateY(-3px)",
                          boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                          borderColor: isDragOver ? "#10b981" : (card.accent || "#10b981"),
                          "& .slot-edit-pill": { opacity: 1 },
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={card.imageUrl}
                        alt={card.label}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Header Controls: Drag Handle & Aspect Ratio Badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.2,
                            py: 0.45,
                            borderRadius: "12px",
                            bgcolor: "rgba(0,0,0,0.65)",
                            backdropFilter: "blur(8px)",
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.85)",
                              borderColor: card.accent || "#10b981",
                            },
                          }}
                        >
                          <DragIndicatorIcon sx={{ color: "#ffffff", fontSize: 16 }} />
                          <Typography sx={{ color: "#fff", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.02em" }}>
                            Drag to move
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            px: 1.1,
                            py: 0.45,
                            borderRadius: "12px",
                            bgcolor: "rgba(0,0,0,0.65)",
                            backdropFilter: "blur(8px)",
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.85)",
                              borderColor: "#34d399",
                            },
                          }}
                        >
                          <AspectRatioOutlinedIcon sx={{ color: "#34d399", fontSize: 15 }} />
                          <Typography sx={{ color: "#fff", fontSize: "0.72rem", fontWeight: 700 }}>
                            Aspect Ratio: {card.aspectRatio || (isSingleLast ? "16:9 (Banner)" : "3:2")}
                          </Typography>
                        </Box>

                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setQuickViewProperty(card);
                            setProductSearch("");
                          }}
                          sx={{
                            px: 1.1,
                            py: 0.45,
                            borderRadius: "12px",
                            bgcolor: "rgba(0,0,0,0.65)",
                            backdropFilter: "blur(8px)",
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.85)",
                              borderColor: "#34d399",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Inventory2OutlinedIcon sx={{ color: "#34d399", fontSize: 14 }} />
                          <Typography sx={{ color: "#fff", fontSize: "0.72rem", fontWeight: 700 }}>
                            {propertyProductCountMap[card.id] || 0} Products
                          </Typography>
                        </Box>
                      </Box>

                      {/* Quick Edit Overlay Tag on Hover */}
                      <Box
                        className="slot-edit-pill"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          px: 1.25,
                          py: 0.45,
                          borderRadius: "12px",
                          bgcolor: "#ffffff",
                          color: "#0f2820",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 14 }} /> Click to Edit
                      </Box>

                      {/* Label at bottom */}
                      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 2, pointerEvents: "none" }}>
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}>
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

                {/* Inline Add Button Card */}
                <Box
                  onClick={() => showAddModal("ADD")}
                  sx={{
                    height: { xs: 200, sm: 260 },
                    borderRadius: "20px",
                    border: "2px dashed #cbd5e1",
                    bgcolor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    p: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#10b981",
                      bgcolor: "#f0fdf4",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "#ecfdf5",
                      color: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <AddIcon />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f2820">
                    Add Another Category
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add to storefront collage
                  </Typography>
                </Box>
              </Box>

              {/* ── 3. Draft / Hidden Categories ── */}
              {inactiveProperties.length > 0 && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #eef2f0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#475569">
                      Draft / Hidden Categories
                    </Typography>
                    <Chip
                      size="small"
                      label={`${inactiveProperties.length} Hidden`}
                      sx={{ height: 20, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    These categories are stored in your database but currently unpublished from the storefront. Click to edit or publish.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: "14px",
                      alignItems: "start",
                    }}
                  >
                    {inactiveProperties.map((card) => {
                      const cardAspect = getCssAspectRatio(card.aspectRatio, "3 / 2");
                      return (
                        <Box
                          key={card.id}
                          onClick={() => setEditProperty(card)}
                          sx={{
                            width: "100%",
                            height: "auto",
                            aspectRatio: cardAspect,
                            borderRadius: "14px",
                            overflow: "hidden",
                            position: "relative",
                            cursor: "pointer",
                            border: "2px dashed #f59e0b",
                            bgcolor: "#fffbeb",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              borderColor: "#d97706",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 20px rgba(245, 158, 11, 0.15)",
                            },
                          }}
                        >
                        {card.imageUrl ? (
                          <Box
                            component="img"
                            src={card.imageUrl}
                            alt={card.label}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.75 }}
                          />
                        ) : (
                          <Box sx={{ width: "100%", height: "100%", bgcolor: "#fef3c7" }} />
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.4) 100%)",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            size="small"
                            label="HIDDEN / DRAFT"
                            sx={{
                              height: 20,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: "rgba(0,0,0,0.7)",
                              color: "#f59e0b",
                              border: "1px solid #f59e0b",
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<AspectRatioOutlinedIcon style={{ fontSize: 11, color: "#fff" }} />}
                            label={`Aspect Ratio: ${card.aspectRatio || "3:2"}`}
                            sx={{
                              height: 20,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: "rgba(0,0,0,0.7)",
                              color: "#ffffff",
                              border: "1px solid rgba(255,255,255,0.3)",
                            }}
                          />
                          <Chip
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickViewProperty(card);
                              setProductSearch("");
                            }}
                            icon={<Inventory2OutlinedIcon style={{ fontSize: 11, color: "#34d399" }} />}
                            label={`${propertyProductCountMap[card.id] || 0} Products`}
                            sx={{
                              height: 20,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: "rgba(0,0,0,0.7)",
                              color: "#ffffff",
                              border: "1px solid rgba(255,255,255,0.3)",
                              cursor: "pointer",
                              "&:hover": { borderColor: "#34d399", bgcolor: "rgba(0,0,0,0.85)" },
                            }}
                          />
                        </Box>
                        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.25 }}>
                          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>
                            {card.label}
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.68rem" }}>
                            Click to Edit & Publish
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Tab 2: Data Table & Reordering */}
      {!loading && !loadError && activeTab === "table" && (
        <Paper elevation={0} sx={{ width: "100%", borderRadius: 4, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              borderBottom: "1px solid #eef2f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#0f2820">
                All Property Categories ({propertiesList.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Toggle switch to publish/unpublish directly. Click any row to edit.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Search properties" size="small" fullWidth value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by category name or ID" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 } }} />
              <TextField select label="Visibility" size="small" value={visibility} onChange={(event) => setVisibility(event.target.value)} sx={{ minWidth: 180 }}><MenuItem value="ALL">All properties</MenuItem><MenuItem value="ACTIVE">Live on store</MenuItem><MenuItem value="HIDDEN">Hidden</MenuItem></TextField>
              {(search || visibility !== "ALL") && <Button onClick={() => { setSearch(""); setVisibility("ALL"); }} sx={{ whiteSpace: "nowrap", textTransform: "none" }}>Clear filters</Button>}
            </Stack>
            <Typography variant="caption" sx={{ display: "block", mt: 2, color: "#64748b" }}>Showing {rows.length} of {propertiesList.length} properties • Click a row to edit</Typography>
          </Box>
          <Box sx={{ width: "100%" }}>
            <CustomDataGrid
              rows={rows}
              columns={columns}
              pageSizes={[10, 25, 50]}
              onRowClickHandle={(params, event) => {
                if (!event.target.closest('a, button, input, [role="switch"]')) setEditProperty(params.row);
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Add / Edit Property Modal */}
      <Modal aria-labelledby="property-editor-title" open={openModal} onClose={hideModal} closeAfterTransition>
        <Fade in={openModal}>
          <Box sx={styles.boxStyle}>
            <AddProperty
              saving={saving}
              hideModal={hideModal}
              handleSetProperty={handleSetProperty}
              action={editModal ? "EDIT" : "ADD"}
              onDelete={
                editModal
                  ? () => {
                      hideModal();
                      setDeleteTarget(propertyContext.state);
                    }
                  : undefined
              }
            />
          </Box>
        </Fade>
      </Modal>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} aria-labelledby="delete-property-title" maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle id="delete-property-title" sx={{ fontWeight: 800 }}>Delete property?</DialogTitle>
        <DialogContent><DialogContentText>Delete "{deleteTarget?.label || "this property"}"? It will be removed from your homepage. This cannot be undone.</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="contained" color="error" disabled={deleting} onClick={() => handleDeleteProperty(deleteTarget.id)}>{deleting ? "Deleting…" : "Delete property"}</Button></DialogActions>
      </Dialog>

      {/* Quick View Linked Products Dialog */}
      {/* Quick View & Direct Product Linking Modal */}
      <Dialog
        open={Boolean(quickViewProperty)}
        onClose={handleCloseQuickView}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="quick-view-property-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "88vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {quickViewProperty && (
          <>
            {/* Header */}
            <DialogTitle
              id="quick-view-property-title"
              sx={{
                p: { xs: 2, sm: 2.5 },
                pb: 1.5,
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
                <Avatar
                  variant="rounded"
                  src={quickViewProperty.imageUrl}
                  alt={quickViewProperty.label}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    border: "1px solid #cbd5e1",
                    bgcolor: "#f1f5f9",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6" fontWeight={800} color="#0f2820" noWrap>
                      {quickViewProperty.label || "Property Products"}
                    </Typography>
                    <Chip
                      label={quickViewProperty.isActive !== false ? "Active" : "Hidden"}
                      size="small"
                      color={quickViewProperty.isActive !== false ? "success" : "default"}
                      sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                    />
                    <Chip
                      icon={<AspectRatioOutlinedIcon style={{ fontSize: 12, color: "#065f46" }} />}
                      label={`Ratio: ${quickViewProperty.aspectRatio || "3:2"}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        bgcolor: "#eef7f4",
                        color: "#065f46",
                        border: "1px solid #bbf7d0",
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.25 }}>
                    {quickViewTab === 0
                      ? `Quick View: Linked Products (${currentPropertyCounts.linked} total)`
                      : `Catalog Linker: Link products directly to "${quickViewProperty.label}"`}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                aria-label="close"
                onClick={handleCloseQuickView}
                size="small"
                sx={{
                  color: "#64748b",
                  "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc", px: 2 }}>
              <Tabs
                value={quickViewTab}
                onChange={(e, val) => {
                  setQuickViewTab(val);
                  setProductSearch("");
                }}
                sx={{
                  minHeight: 44,
                  "& .MuiTab-root": {
                    minHeight: 44,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: "#64748b",
                    "&.Mui-selected": {
                      color: "#004d40",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    bgcolor: "#004d40",
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab
                  icon={<Inventory2OutlinedIcon sx={{ fontSize: "16px !important" }} />}
                  iconPosition="start"
                  label={`Linked Products (${currentPropertyCounts.linked})`}
                  id="tab-linked-products"
                />
                <Tab
                  icon={<AddLinkRoundedIcon sx={{ fontSize: "17px !important" }} />}
                  iconPosition="start"
                  label={`Link Products (${currentPropertyCounts.unlinked} available)`}
                  id="tab-link-products"
                />
              </Tabs>
            </Box>

            {/* TAB 0: LINKED PRODUCTS VIEW */}
            {quickViewTab === 0 && (
              <>
                {/* Search Bar for Linked Products */}
                {linkedProductsForQuickView.length > 0 && (
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      pb: 1,
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Filter linked products by title, SKU, or category…"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: productSearch ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label="clear product search"
                              onClick={() => setProductSearch("")}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        bgcolor: "#ffffff",
                        borderRadius: 1.5,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                        px: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="#64748b" fontWeight={600}>
                        Showing {filteredLinkedProducts.length} of {linkedProductsForQuickView.length} linked products
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddLinkRoundedIcon sx={{ fontSize: "15px !important" }} />}
                        onClick={() => {
                          setQuickViewTab(1);
                          setProductSearch("");
                        }}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, p: 0, color: "#004d40" }}
                      >
                        + Link More Products
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Content list for Linked Products */}
                <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: "#f8fafc" }}>
                  {linkedProductsForQuickView.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2.5, sm: 4 },
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#ffffff",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {/* Decorative background glow */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: -60,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 340,
                          height: 180,
                          borderRadius: "50%",
                          background:
                            "radial-gradient(ellipse at center, rgba(16, 185, 129, 0.12) 0%, rgba(255, 255, 255, 0) 70%)",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Layered Clickable Icon Illustration */}
                      <Tooltip title="Click to open products to link" arrow>
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label="Open products to link"
                          onClick={() => {
                            setQuickViewTab(1);
                            setProductSearch("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setQuickViewTab(1);
                              setProductSearch("");
                            }
                          }}
                          sx={{
                            position: "relative",
                            mb: 2.5,
                            cursor: "pointer",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            outline: "none",
                            "&:hover": {
                              transform: "translateY(-3px) scale(1.04)",
                              "& .outer-ring": {
                                borderColor: "#10b981",
                                bgcolor: "#d1fae5",
                                boxShadow: "0 12px 28px rgba(16, 185, 129, 0.25)",
                              },
                              "& .inner-badge": {
                                boxShadow: "0 8px 20px rgba(0, 77, 64, 0.35)",
                                bgcolor: "#00382e",
                              },
                              "& .floating-link-badge": {
                                transform: "scale(1.15)",
                                borderColor: "#34d399",
                                bgcolor: "#ecfdf5",
                                color: "#004d40",
                              },
                            },
                            "&:focus-visible": {
                              "& .outer-ring": {
                                outline: "2px solid #004d40",
                              },
                            },
                          }}
                        >
                          <Box
                            className="outer-ring"
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              bgcolor: "#ecfdf5",
                              border: "2px dashed #a7f3d0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.15)",
                              transition: "all 0.25s ease",
                            }}
                          >
                            <Box
                              className="inner-badge"
                              sx={{
                                width: 54,
                                height: 54,
                                borderRadius: "50%",
                                bgcolor: "#004d40",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#34d399",
                                boxShadow: "0 4px 12px rgba(0, 77, 64, 0.25)",
                                transition: "all 0.25s ease",
                              }}
                            >
                              <Inventory2OutlinedIcon sx={{ fontSize: 28 }} />
                            </Box>
                          </Box>
                          <Box
                            className="floating-link-badge"
                            sx={{
                              position: "absolute",
                              bottom: -2,
                              right: -2,
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              bgcolor: "#ffffff",
                              border: "2px solid #ecfdf5",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#059669",
                              transition: "all 0.25s ease",
                            }}
                          >
                            <AddLinkRoundedIcon sx={{ fontSize: 16 }} />
                          </Box>
                        </Box>
                      </Tooltip>

                      {/* Status Chip */}
                      <Chip
                        label="0 Products Linked"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#fef3c7",
                          color: "#92400e",
                          border: "1px solid #fde68a",
                          mb: 1.5,
                        }}
                      />

                      {/* Heading */}
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color="#0f2820"
                        sx={{ letterSpacing: "-0.01em", mb: 3 }}
                      >
                        No Products Linked Yet
                      </Typography>

                      {/* 3 Quick Benefit Cards */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                          gap: 1.5,
                          maxWidth: 640,
                          width: "100%",
                          mb: 3.5,
                          textAlign: "left",
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "#004d40" }}>
                            <TouchAppOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" fontWeight={800} color="#0f2820">
                              Instant Linking
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.72rem", lineHeight: 1.4, display: "block" }}>
                            Link or unlink products in 1 click from your store catalog.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "#004d40" }}>
                            <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" fontWeight={800} color="#0f2820">
                              Multi-Property
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.72rem", lineHeight: 1.4, display: "block" }}>
                            Each product can belong to multiple categories simultaneously.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "#004d40" }}>
                            <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" fontWeight={800} color="#0f2820">
                              Homepage Sync
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.72rem", lineHeight: 1.4, display: "block" }}>
                            Assigned items immediately reflect on the live storefront.
                          </Typography>
                        </Box>
                      </Box>

                      {/* Suggested Products from Catalog */}
                      {suggestedProductsForEmptyState.length > 0 && (
                        <Box sx={{ width: "100%", maxWidth: 640, mb: 3, textAlign: "left" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25, px: 0.5 }}>
                            <Typography variant="caption" fontWeight={800} color="#475569" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                              Suggested from Catalog to Get Started:
                            </Typography>
                            <Typography variant="caption" color="#94a3b8" sx={{ fontSize: "0.7rem" }}>
                              1-Click Quick Link
                            </Typography>
                          </Box>

                          <Stack spacing={1}>
                            {suggestedProductsForEmptyState.map((prod) => {
                              const imgUrl = (Array.isArray(prod.images) && prod.images[0]?.url) || prod.imageUrl || prod.image || "";
                              const priceVal = prod.sellingPrice ?? prod.price ?? prod.mrp;
                              const isBusy = linkingProductId === prod.id;

                              return (
                                <Box
                                  key={prod.id}
                                  sx={{
                                    p: 1,
                                    px: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid #e2e8f0",
                                    bgcolor: "#f8fafc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                      borderColor: "#a7f3d0",
                                      bgcolor: "#f0fdf4",
                                    },
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                    <Tooltip title="Click to view product details" arrow>
                                      <Avatar
                                        variant="rounded"
                                        src={imgUrl}
                                        alt={prod.title}
                                        onClick={() => {
                                          setQuickProductDetail(prod);
                                          setQuickDetailImageIndex(0);
                                        }}
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 1.25,
                                          border: "1px solid #e2e8f0",
                                          bgcolor: "#fff",
                                          flexShrink: 0,
                                          cursor: "pointer",
                                          transition: "transform 0.15s ease",
                                          "&:hover": {
                                            transform: "scale(1.08)",
                                            borderColor: "#004d40",
                                          },
                                        }}
                                      >
                                        <Inventory2OutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                                      </Avatar>
                                    </Tooltip>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Tooltip title="Click to view product details" arrow placement="top-start">
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          color="#0f172a"
                                          noWrap
                                          onClick={() => {
                                            setQuickProductDetail(prod);
                                            setQuickDetailImageIndex(0);
                                          }}
                                          sx={{
                                            maxWidth: { xs: 180, sm: 320 },
                                            cursor: "pointer",
                                            transition: "color 0.15s ease",
                                            "&:hover": {
                                              color: "#004d40",
                                              textDecoration: "underline",
                                            },
                                          }}
                                        >
                                          {prod.title}
                                        </Typography>
                                      </Tooltip>
                                      <Typography variant="caption" color="#64748b">
                                        {prod.parentCategory?.name || (priceVal ? `₹${priceVal}` : "In Catalog")}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <AddLinkRoundedIcon sx={{ fontSize: "14px !important" }} />}
                                    disabled={isBusy}
                                    onClick={() => handleToggleLinkProduct(prod, quickViewProperty)}
                                    sx={{
                                      height: 28,
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      textTransform: "none",
                                      borderRadius: 1.5,
                                      px: 1.5,
                                      bgcolor: "#004d40",
                                      color: "#fff",
                                      flexShrink: 0,
                                      "&:hover": { bgcolor: "#00382e" },
                                    }}
                                  >
                                    + Link
                                  </Button>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  ) : filteredLinkedProducts.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <Typography variant="body1" fontWeight={700} color="#334155">
                        No products matched "{productSearch}"
                      </Typography>
                      <Typography variant="body2" color="#64748b" sx={{ mt: 0.5, mb: 2 }}>
                        Try searching with another keyword, SKU, or category.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setProductSearch("")}
                        sx={{ textTransform: "none" }}
                      >
                        Clear Filter
                      </Button>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {filteredLinkedProducts.map((prod) => {
                        const imgUrl =
                          (Array.isArray(prod.images) && prod.images[0]?.url) ||
                          prod.imageUrl ||
                          prod.image ||
                          "";
                        const units = Number(prod.unitsInStock) || 0;
                        const isOutOfStock = units <= 0;
                        const isLowStock = !isOutOfStock && units <= 5;
                        const stockLabel = isOutOfStock
                          ? "Out of Stock"
                          : isLowStock
                          ? `Low Stock (${units})`
                          : `In Stock (${units})`;
                        const stockBg = isOutOfStock ? "#fef2f2" : isLowStock ? "#fffbeb" : "#ecfdf5";
                        const stockColor = isOutOfStock ? "#dc2626" : isLowStock ? "#d97706" : "#059669";
                        const priceVal = prod.sellingPrice ?? prod.price ?? prod.mrp;

                        return (
                          <Paper
                            key={prod.id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid #e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: "#cbd5e1",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                              <Tooltip title="Click to view product details" arrow>
                                <Avatar
                                  variant="rounded"
                                  src={imgUrl}
                                  alt={prod.title}
                                  onClick={() => {
                                    setQuickProductDetail(prod);
                                    setQuickDetailImageIndex(0);
                                  }}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 1.5,
                                    border: "1px solid #e2e8f0",
                                    bgcolor: "#ffffff",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                    "&:hover": {
                                      transform: "scale(1.08)",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                      borderColor: "#004d40",
                                    },
                                  }}
                                >
                                  <Inventory2OutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                </Avatar>
                              </Tooltip>
                              <Box sx={{ minWidth: 0 }}>
                                <Tooltip title="Click to view product details" arrow placement="top-start">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="#0f172a"
                                    noWrap
                                    onClick={() => {
                                      setQuickProductDetail(prod);
                                      setQuickDetailImageIndex(0);
                                    }}
                                    sx={{
                                      maxWidth: { xs: 160, sm: 280, md: 380 },
                                      cursor: "pointer",
                                      transition: "color 0.15s ease",
                                      "&:hover": {
                                        color: "#004d40",
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {prod.title}
                                  </Typography>
                                </Tooltip>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
                                  {prod.id && (
                                    <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace" }}>
                                      SKU: {prod.id}
                                    </Typography>
                                  )}
                                  {prod.parentCategory?.name && (
                                    <Chip
                                      label={prod.parentCategory.name}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        bgcolor: "#f1f5f9",
                                        color: "#475569",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
                              <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                                {priceVal !== undefined && priceVal !== null && (
                                  <Typography variant="body2" fontWeight={800} color="#0f172a">
                                    ₹{priceVal}
                                  </Typography>
                                )}
                                <Chip
                                  label={stockLabel}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: stockBg,
                                    color: stockColor,
                                    border: `1px solid ${stockColor}33`,
                                    mt: 0.25,
                                  }}
                                />
                              </Box>

                              <Tooltip title={`Unlink from ${quickViewProperty.label}`} arrow>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={
                                    linkingProductId === prod.id ? (
                                      <CircularProgress size={12} color="inherit" />
                                    ) : (
                                      <LinkOffRoundedIcon sx={{ fontSize: "14px !important" }} />
                                    )
                                  }
                                  disabled={linkingProductId === prod.id}
                                  onClick={() => handleToggleLinkProduct(prod, quickViewProperty)}
                                  sx={{
                                    height: 28,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    borderRadius: 1.5,
                                    borderColor: "#fecaca",
                                    color: "#dc2626",
                                    px: 1,
                                    "&:hover": {
                                      borderColor: "#dc2626",
                                      bgcolor: "#fee2e2",
                                    },
                                  }}
                                >
                                  Unlink
                                </Button>
                              </Tooltip>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </DialogContent>
              </>
            )}

            {/* TAB 1: CATALOG LINKER VIEW */}
            {quickViewTab === 1 && (
              <>
                {/* Search & Filter Toolbar for Catalog Products */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    pb: 1.5,
                    bgcolor: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search catalog by title, SKU, or category to link…"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: productSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="clear catalog search"
                            onClick={() => setProductSearch("")}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      bgcolor: "#ffffff",
                      borderRadius: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1.5,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={`All Products (${products.length})`}
                        size="small"
                        onClick={() => setLinkFilter("ALL")}
                        color={linkFilter === "ALL" ? "primary" : "default"}
                        variant={linkFilter === "ALL" ? "filled" : "outlined"}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          height: 24,
                          cursor: "pointer",
                          ...(linkFilter === "ALL" && { bgcolor: "#004d40", color: "#fff" }),
                        }}
                      />
                      <Chip
                        label={`Unlinked (${currentPropertyCounts.unlinked})`}
                        size="small"
                        onClick={() => setLinkFilter("UNLINKED")}
                        color={linkFilter === "UNLINKED" ? "primary" : "default"}
                        variant={linkFilter === "UNLINKED" ? "filled" : "outlined"}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          height: 24,
                          cursor: "pointer",
                          ...(linkFilter === "UNLINKED" && { bgcolor: "#004d40", color: "#fff" }),
                        }}
                      />
                      <Chip
                        label={`Linked (${currentPropertyCounts.linked})`}
                        size="small"
                        onClick={() => setLinkFilter("LINKED")}
                        color={linkFilter === "LINKED" ? "primary" : "default"}
                        variant={linkFilter === "LINKED" ? "filled" : "outlined"}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          height: 24,
                          cursor: "pointer",
                          ...(linkFilter === "LINKED" && { bgcolor: "#004d40", color: "#fff" }),
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="#64748b" fontWeight={600}>
                      Showing {catalogProductsForLinkTab.length} products
                    </Typography>
                  </Box>
                </Box>

                {/* Content list for Catalog Linker */}
                <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: "#f8fafc" }}>
                  {products.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Inventory2OutlinedIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1.5 }} />
                      <Typography variant="h6" fontWeight={700} color="#0f2820">
                        No Products in Catalog
                      </Typography>
                      <Typography variant="body2" color="#64748b" sx={{ maxWidth: 380, mx: "auto", mt: 0.5, mb: 2 }}>
                        Add products to your catalog first before linking them to properties.
                      </Typography>
                      <Button
                        variant="contained"
                        component="a"
                        href="/admin/products"
                        sx={{ bgcolor: "#004d40", textTransform: "none", fontWeight: 700 }}
                      >
                        Open Products Catalog
                      </Button>
                    </Box>
                  ) : catalogProductsForLinkTab.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <Typography variant="body1" fontWeight={700} color="#334155">
                        No products match the current filters
                      </Typography>
                      <Typography variant="body2" color="#64748b" sx={{ mt: 0.5, mb: 2 }}>
                        Try clearing search terms or switching between All / Unlinked / Linked.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setProductSearch("");
                          setLinkFilter("ALL");
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Reset Filters
                      </Button>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {catalogProductsForLinkTab.map((prod) => {
                        const isLinked = isProductLinkedToProperty(prod, quickViewProperty);
                        const imgUrl =
                          (Array.isArray(prod.images) && prod.images[0]?.url) ||
                          prod.imageUrl ||
                          prod.image ||
                          "";
                        const units = Number(prod.unitsInStock) || 0;
                        const isOutOfStock = units <= 0;
                        const isLowStock = !isOutOfStock && units <= 5;
                        const stockLabel = isOutOfStock
                          ? "Out of Stock"
                          : isLowStock
                          ? `Low Stock (${units})`
                          : `In Stock (${units})`;
                        const stockBg = isOutOfStock ? "#fef2f2" : isLowStock ? "#fffbeb" : "#ecfdf5";
                        const stockColor = isOutOfStock ? "#dc2626" : isLowStock ? "#d97706" : "#059669";
                        const priceVal = prod.sellingPrice ?? prod.price ?? prod.mrp;

                        return (
                          <Paper
                            key={prod.id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: isLinked ? "#bbf7d0" : "#e2e8f0",
                              bgcolor: isLinked ? "#fafffd" : "#ffffff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: isLinked ? "#86efac" : "#cbd5e1",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                              <Tooltip title="Click to view product details" arrow>
                                <Avatar
                                  variant="rounded"
                                  src={imgUrl}
                                  alt={prod.title}
                                  onClick={() => {
                                    setQuickProductDetail(prod);
                                    setQuickDetailImageIndex(0);
                                  }}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 1.5,
                                    border: "1px solid #e2e8f0",
                                    bgcolor: "#ffffff",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                                    "&:hover": {
                                      transform: "scale(1.08)",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                      borderColor: "#004d40",
                                    },
                                  }}
                                >
                                  <Inventory2OutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                </Avatar>
                              </Tooltip>
                              <Box sx={{ minWidth: 0 }}>
                                <Tooltip title="Click to view product details" arrow placement="top-start">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="#0f172a"
                                    noWrap
                                    onClick={() => {
                                      setQuickProductDetail(prod);
                                      setQuickDetailImageIndex(0);
                                    }}
                                    sx={{
                                      maxWidth: { xs: 160, sm: 280, md: 380 },
                                      cursor: "pointer",
                                      transition: "color 0.15s ease",
                                      "&:hover": {
                                        color: "#004d40",
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {prod.title}
                                  </Typography>
                                </Tooltip>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
                                  {prod.id && (
                                    <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace" }}>
                                      SKU: {prod.id}
                                    </Typography>
                                  )}
                                  {prod.parentCategory?.name && (
                                    <Chip
                                      label={prod.parentCategory.name}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        bgcolor: "#f1f5f9",
                                        color: "#475569",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                              <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                                {priceVal !== undefined && priceVal !== null && (
                                  <Typography variant="body2" fontWeight={800} color="#0f172a">
                                    ₹{priceVal}
                                  </Typography>
                                )}
                                <Chip
                                  label={stockLabel}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: stockBg,
                                    color: stockColor,
                                    border: `1px solid ${stockColor}33`,
                                    mt: 0.25,
                                  }}
                                />
                              </Box>

                              {isLinked ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip
                                    icon={<CheckCircleRoundedIcon sx={{ fontSize: "14px !important", color: "#065f46" }} />}
                                    label="Linked"
                                    size="small"
                                    sx={{
                                      height: 28,
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      bgcolor: "#ecfdf5",
                                      color: "#065f46",
                                      border: "1px solid #a7f3d0",
                                    }}
                                  />
                                  <Tooltip title={`Unlink from ${quickViewProperty.label}`} arrow>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={
                                        linkingProductId === prod.id ? (
                                          <CircularProgress size={12} color="inherit" />
                                        ) : (
                                          <LinkOffRoundedIcon sx={{ fontSize: "14px !important" }} />
                                        )
                                      }
                                      disabled={linkingProductId === prod.id}
                                      onClick={() => handleToggleLinkProduct(prod, quickViewProperty)}
                                      sx={{
                                        height: 28,
                                        fontSize: "0.72rem",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        borderRadius: 1.5,
                                        borderColor: "#fecaca",
                                        color: "#dc2626",
                                        px: 1,
                                        "&:hover": {
                                          borderColor: "#dc2626",
                                          bgcolor: "#fee2e2",
                                        },
                                      }}
                                    >
                                      Unlink
                                    </Button>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={
                                    linkingProductId === prod.id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <AddLinkRoundedIcon sx={{ fontSize: "16px !important" }} />
                                    )
                                  }
                                  disabled={linkingProductId === prod.id}
                                  onClick={() => handleToggleLinkProduct(prod, quickViewProperty)}
                                  sx={{
                                    height: 28,
                                    fontSize: "0.74rem",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    borderRadius: 1.5,
                                    px: 1.75,
                                    bgcolor: "#004d40",
                                    color: "#ffffff",
                                    boxShadow: "0 2px 6px rgba(0,77,64,0.15)",
                                    "&:hover": {
                                      bgcolor: "#00382e",
                                    },
                                  }}
                                >
                                  + Link Product
                                </Button>
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </DialogContent>
              </>
            )}

            {/* Footer */}
            <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="#64748b" fontWeight={600}>
                  {quickViewProperty.label}: <strong style={{ color: "#004d40" }}>{currentPropertyCounts.linked}</strong> products linked
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {quickViewTab === 0 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddLinkRoundedIcon />}
                    onClick={() => {
                      setQuickViewTab(1);
                      setProductSearch("");
                    }}
                    sx={{
                      textTransform: "none",
                      color: "#004d40",
                      borderColor: "#004d40",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Link More Products
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={() => {
                      setQuickViewTab(0);
                      setProductSearch("");
                    }}
                    sx={{
                      textTransform: "none",
                      color: "#004d40",
                      borderColor: "#004d40",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    View Linked ({currentPropertyCounts.linked})
                  </Button>
                )}
                <Button
                  onClick={handleCloseQuickView}
                  sx={{ textTransform: "none", color: "#64748b", fontWeight: 700 }}
                >
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Quick Product Detail Modal */}
      <Dialog
        open={Boolean(activeProductDetail)}
        onClose={() => setQuickProductDetail(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="product-quick-detail-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: { xs: 0.5, sm: 1 },
          },
        }}
      >
        {activeProductDetail && (() => {
          const detailImages = (Array.isArray(activeProductDetail.images) && activeProductDetail.images.length > 0)
            ? activeProductDetail.images.map((img) => (typeof img === "string" ? img : img?.url)).filter(Boolean)
            : (activeProductDetail.imageUrl || activeProductDetail.image)
            ? [activeProductDetail.imageUrl || activeProductDetail.image]
            : [];
          const mainImg = detailImages[quickDetailImageIndex] || detailImages[0] || "";
          const units = Number(activeProductDetail.unitsInStock) || 0;
          const isOutOfStock = units <= 0;
          const isLowStock = !isOutOfStock && units <= 5;
          const stockLabel = isOutOfStock
            ? "Out of Stock"
            : isLowStock
            ? `Low Stock (${units})`
            : `In Stock (${units})`;
          const stockBg = isOutOfStock ? "#fef2f2" : isLowStock ? "#fffbeb" : "#ecfdf5";
          const stockColor = isOutOfStock ? "#dc2626" : isLowStock ? "#d97706" : "#059669";
          const isLinkedToCurrent = quickViewProperty ? isProductLinkedToProperty(activeProductDetail, quickViewProperty) : false;
          const priceVal = activeProductDetail.sellingPrice ?? activeProductDetail.listPrice ?? activeProductDetail.price;
          const mrpVal = activeProductDetail.maxPrice ?? activeProductDetail.mrp;
          const hasDiscount = Number(mrpVal) > Number(priceVal) && Number(priceVal) > 0;
          const discountPct = hasDiscount ? Math.round(((Number(mrpVal) - Number(priceVal)) / Number(mrpVal)) * 100) : 0;
          const rawDesc = activeProductDetail.shortDescription || activeProductDetail.description || "";
          const plainDesc = stripHtml(rawDesc);

          // Assigned properties list names
          const propNames = Array.isArray(activeProductDetail.properties)
            ? activeProductDetail.properties.map((p) => {
                if (!p) return "";
                if (typeof p === "string") {
                  const match = propertiesList.find((item) => item.id === p || item.label?.toLowerCase() === p.toLowerCase());
                  return match?.label || p;
                }
                return p.label || p.title || p.id || "";
              }).filter(Boolean)
            : [];

          return (
            <>
              <DialogTitle
                id="product-quick-detail-title"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  pb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar sx={{ bgcolor: "#e6f4ea", color: "#004d40", width: 36, height: 36 }}>
                    <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                      Product Quick Preview
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      SKU: {activeProductDetail.id || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setQuickProductDetail(null)}
                  aria-label="Close product details"
                  sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Images Preview Section */}
                <Box>
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 200, sm: 240 },
                      borderRadius: 2.5,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {mainImg ? (
                      <Box
                        component="img"
                        src={mainImg}
                        alt={activeProductDetail.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          p: 1.5,
                        }}
                      />
                    ) : (
                      <Box sx={{ textAlign: "center", color: "#94a3b8" }}>
                        <Inventory2OutlinedIcon sx={{ fontSize: 48, mb: 0.5, opacity: 0.5 }} />
                        <Typography variant="caption" display="block">No Image Available</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Thumbnail Row if multiple images */}
                  {detailImages.length > 1 && (
                    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pt: 1.25, pb: 0.5 }}>
                      {detailImages.map((img, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          onClick={() => setQuickDetailImageIndex(idx)}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1.5,
                            objectFit: "cover",
                            cursor: "pointer",
                            border: "2px solid",
                            borderColor: idx === quickDetailImageIndex ? "#004d40" : "#e2e8f0",
                            opacity: idx === quickDetailImageIndex ? 1 : 0.65,
                            transition: "all 0.15s ease",
                            "&:hover": { opacity: 1, borderColor: "#004d40" },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Title & Metadata */}
                <Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.75, mb: 1 }}>
                    {activeProductDetail.parentCategory?.name && (
                      <Chip
                        label={activeProductDetail.parentCategory.name}
                        size="small"
                        sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700, fontSize: "0.72rem" }}
                      />
                    )}
                    <Chip
                      label={stockLabel}
                      size="small"
                      sx={{
                        bgcolor: stockBg,
                        color: stockColor,
                        fontWeight: 700,
                        border: `1px solid ${stockColor}33`,
                        fontSize: "0.72rem",
                      }}
                    />
                    {hasDiscount && (
                      <Chip
                        label={`${discountPct}% OFF`}
                        size="small"
                        sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 800, fontSize: "0.72rem" }}
                      />
                    )}
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>
                    {activeProductDetail.title}
                  </Typography>
                  {(activeProductDetail.netQuantity || activeProductDetail.unitType) && (
                    <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                      Package Size: {activeProductDetail.netQuantity || ""} {activeProductDetail.unitType || ""}
                    </Typography>
                  )}
                </Box>

                {/* Pricing & Stock Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    borderRadius: 2.5,
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    textAlign: "center",
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Selling Price
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#004d40" }}>
                      {formatINR(priceVal)}
                    </Typography>
                  </Box>
                  {mrpVal !== undefined && mrpVal !== null && (
                    <Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Printed MRP
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#94a3b8", textDecoration: "line-through" }}>
                        {formatINR(mrpVal)}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Available Units
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: stockColor }}>
                      {units}
                    </Typography>
                  </Box>
                </Paper>

                {/* Property Linking Status Banner with Direct Link Action */}
                {quickViewProperty && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      bgcolor: isLinkedToCurrent ? "#ecfdf5" : "#f8fafc",
                      border: "1px solid",
                      borderColor: isLinkedToCurrent ? "#a7f3d0" : "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      {isLinkedToCurrent ? (
                        <CheckCircleRoundedIcon sx={{ color: "#059669", fontSize: 24 }} />
                      ) : (
                        <StorefrontOutlinedIcon sx={{ color: "#64748b", fontSize: 24 }} />
                      )}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isLinkedToCurrent ? "#065f46" : "#334155" }}>
                          {isLinkedToCurrent
                            ? `Linked to "${quickViewProperty.label}"`
                            : `Not linked to "${quickViewProperty.label}"`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isLinkedToCurrent ? "#047857" : "#64748b" }}>
                          {isLinkedToCurrent
                            ? "This product is displayed in this property showcase."
                            : "Click link to show this product under this property."}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      size="small"
                      variant={isLinkedToCurrent ? "outlined" : "contained"}
                      color={isLinkedToCurrent ? "error" : "primary"}
                      disabled={linkingProductId === activeProductDetail.id}
                      startIcon={
                        linkingProductId === activeProductDetail.id ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : isLinkedToCurrent ? (
                          <LinkOffRoundedIcon sx={{ fontSize: "14px !important" }} />
                        ) : (
                          <AddLinkRoundedIcon sx={{ fontSize: "14px !important" }} />
                        )
                      }
                      onClick={() => handleToggleLinkProduct(activeProductDetail, quickViewProperty)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 32,
                        borderRadius: 2,
                        px: 2,
                        ...(isLinkedToCurrent
                          ? { borderColor: "#fecaca", color: "#dc2626", "&:hover": { bgcolor: "#fee2e2" } }
                          : { bgcolor: "#004d40", "&:hover": { bgcolor: "#00382e" } }),
                      }}
                    >
                      {isLinkedToCurrent ? "Unlink Property" : "+ Link to Property"}
                    </Button>
                  </Paper>
                )}

                {/* Assigned Properties Chips */}
                {propNames.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Assigned Property Categories:
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75, mt: 0.75 }}>
                      {propNames.map((name, i) => (
                        <Chip
                          key={i}
                          label={name}
                          size="small"
                          sx={{
                            bgcolor: name.toLowerCase() === quickViewProperty?.label?.toLowerCase() ? "#dcfce7" : "#f1f5f9",
                            color: name.toLowerCase() === quickViewProperty?.label?.toLowerCase() ? "#166534" : "#334155",
                            fontWeight: 700,
                            border: "1px solid",
                            borderColor: name.toLowerCase() === quickViewProperty?.label?.toLowerCase() ? "#86efac" : "#e2e8f0",
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Description Preview */}
                {plainDesc && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Description:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                        border: "1px solid #f1f5f9",
                        color: "#334155",
                        lineHeight: 1.6,
                        maxHeight: 110,
                        overflowY: "auto",
                      }}
                    >
                      {plainDesc}
                    </Typography>
                  </Box>
                )}

                {/* Marketplace Links */}
                {(activeProductDetail.amazonURL || activeProductDetail.flipkartURL || activeProductDetail.tata1mgURL) && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75, display: "block" }}>
                      Marketplace Listings:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                      {activeProductDetail.amazonURL && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                          href={activeProductDetail.amazonURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.72rem" }}
                        >
                          Amazon
                        </Button>
                      )}
                      {activeProductDetail.flipkartURL && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                          href={activeProductDetail.flipkartURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.72rem" }}
                        >
                          Flipkart
                        </Button>
                      )}
                      {activeProductDetail.tata1mgURL && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                          href={activeProductDetail.tata1mgURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.72rem" }}
                        >
                          Tata 1mg
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2, borderTop: "1px solid #e2e8f0", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={() => setQuickProductDetail(null)}
                  sx={{
                    bgcolor: "#004d40",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    "&:hover": { bgcolor: "#00382e" },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
      {/* Notification Snackbar */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={4000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type}
          sx={{ width: "100%" }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertiesListAdmin;
